import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { THEME_MODES } from '@/utils/constants';

interface UIState {
  theme: {
    mode: 'light' | 'dark';
    primaryColor: string;
    secondaryColor: string;
  };
  sidebar: {
    isOpen: boolean;
    isCollapsed: boolean;
    isMobile: boolean;
  };
  notifications: {
    isOpen: boolean;
    unreadCount: number;
  };
  modals: {
    [key: string]: boolean;
  };
  loading: {
    [key: string]: boolean;
  };
  breadcrumbs: Array<{
    label: string;
    path?: string;
  }>;
}

const initialState: UIState = {
  theme: {
    mode: 'light',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
  },
  sidebar: {
    isOpen: true,
    isCollapsed: false,
    isMobile: false,
  },
  notifications: {
    isOpen: false,
    unreadCount: 0,
  },
  modals: {},
  loading: {},
  breadcrumbs: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme
    setThemeMode: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme.mode = action.payload;
    },
    toggleTheme: (state) => {
      state.theme.mode = state.theme.mode === 'light' ? 'dark' : 'light';
    },
    setPrimaryColor: (state, action: PayloadAction<string>) => {
      state.theme.primaryColor = action.payload;
    },
    setSecondaryColor: (state, action: PayloadAction<string>) => {
      state.theme.secondaryColor = action.payload;
    },

    // Sidebar
    toggleSidebar: (state) => {
      state.sidebar.isOpen = !state.sidebar.isOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isOpen = action.payload;
    },
    toggleSidebarCollapse: (state) => {
      state.sidebar.isCollapsed = !state.sidebar.isCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isCollapsed = action.payload;
    },
    setSidebarMobile: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isMobile = action.payload;
      if (action.payload) {
        state.sidebar.isOpen = false;
      }
    },

    // Notifications
    toggleNotifications: (state) => {
      state.notifications.isOpen = !state.notifications.isOpen;
    },
    setNotificationsOpen: (state, action: PayloadAction<boolean>) => {
      state.notifications.isOpen = action.payload;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.notifications.unreadCount = action.payload;
    },
    incrementUnreadCount: (state) => {
      state.notifications.unreadCount += 1;
    },
    decrementUnreadCount: (state) => {
      if (state.notifications.unreadCount > 0) {
        state.notifications.unreadCount -= 1;
      }
    },

    // Modals
    openModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = false;
    },
    toggleModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = !state.modals[action.payload];
    },
    closeAllModals: (state) => {
      state.modals = {};
    },

    // Loading states
    setLoading: (state, action: PayloadAction<{ key: string; loading: boolean }>) => {
      state.loading[action.payload.key] = action.payload.loading;
    },
    clearLoading: (state, action: PayloadAction<string>) => {
      delete state.loading[action.payload];
    },
    clearAllLoading: (state) => {
      state.loading = {};
    },

    // Breadcrumbs
    setBreadcrumbs: (state, action: PayloadAction<Array<{ label: string; path?: string }>>) => {
      state.breadcrumbs = action.payload;
    },
    addBreadcrumb: (state, action: PayloadAction<{ label: string; path?: string }>) => {
      state.breadcrumbs.push(action.payload);
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = [];
    },
  },
});

export const {
  // Theme
  setThemeMode,
  toggleTheme,
  setPrimaryColor,
  setSecondaryColor,

  // Sidebar
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapse,
  setSidebarCollapsed,
  setSidebarMobile,

  // Notifications
  toggleNotifications,
  setNotificationsOpen,
  setUnreadCount,
  incrementUnreadCount,
  decrementUnreadCount,

  // Modals
  openModal,
  closeModal,
  toggleModal,
  closeAllModals,

  // Loading
  setLoading,
  clearLoading,
  clearAllLoading,

  // Breadcrumbs
  setBreadcrumbs,
  addBreadcrumb,
  clearBreadcrumbs,
} = uiSlice.actions;

export default uiSlice.reducer;